import { Injectable, inject } from '@angular/core';

import { of, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { filterBoolean, WorkerPool } from '@core/common-legacy';
import { HttpApiService } from '@core/http-legacy';
import type { PaginationMeta, Position } from '@core/http-types';
import {
  InsertFromTemplateMethod,
  JobBasic,
  OfferBrief,
  OfferData,
  OfferFull,
} from '@core/offers-types';
import { PlacementMovement } from '@core/stages-types';
import { getOptions } from '@core/walkthrough-util';

import type { OfferScope } from '../../types';

@Injectable({
  providedIn: 'root',
})
export class JobsHttpService {
  private readonly httpApi = inject(HttpApiService);
  private readonly workerPool = inject(WorkerPool);

  public fetchCollection$(scope: OfferScope) {
    return this.httpApi
      .get(`/offers`, { kind: 'job', scope }, getOptions({ hasOnboarding: true }))
      .pipe(
        filterBoolean,
        switchMap(data =>
          data.references
            ? this.workerPool.fillInReferences(data.offers, data.references)
            : of(data.offers),
        ),
        map(offers => ({ offers })),
      );
  }

  // Returns all offers
  public fetchBasicCollection$(
    page: number,
  ): Observable<{ offers: JobBasic[]; meta: PaginationMeta }> {
    return this.httpApi
      .get(
        `/offers`,
        { include: ['location_ids', 'counters', 'work_model', 'issues'], page },
        getOptions({ hasOnboarding: true }),
      )
      .pipe(
        filterBoolean,
        map(response => ({ offers: response.offers, meta: response.meta })),
      );
  }

  public fetchBriefCollection$(scope: OfferScope): Observable<{ offers: OfferBrief[] }> {
    return this.httpApi
      .get('/offers', { kind: 'job', scope, viewMode: 'brief' })
      .pipe(filterBoolean);
  }

  public fetchItem$(params: {
    id: number | string;
    langCode?: string;
  }): Observable<{ offer: OfferFull }> {
    const body = { lang_code: params.langCode };
    return this.httpApi
      .get(`/offers/${params.id}`, body, getOptions({ hasOnboarding: true }))
      .pipe(filterBoolean);
  }

  public create$(data: { offer: OfferData }): Observable<{ offer: OfferFull }> {
    return this.httpApi.post('/offers/', null, data);
  }

  public createFromRequisition$(requisitionId: number): Observable<{ offer: OfferFull }> {
    return this.httpApi.post('/offers/', null, { requisitionId });
  }

  public createFromTemplate$(offerTemplateId: number): Observable<{ offer: OfferFull }> {
    return this.httpApi.post('/offers/', null, { offerTemplateId });
  }

  public createFromPosition$(positionId: number): Observable<{ offer: OfferFull }> {
    return this.httpApi.post('/offers/', null, { positionId });
  }

  public update$(data: { offer: OfferData }): Observable<{ offer: OfferFull }> {
    return this.httpApi.patch(`/offers/${data.offer.id}`, null, data);
  }

  public delete$(params: { id: number }) {
    return this.httpApi.delete(`/offers/${params.id}`);
  }

  public follow(id: string | number): Observable<{ offer: OfferFull }> {
    return this.httpApi.patch(`/offers/${id}/follow`);
  }

  public unfollow(id: string | number): Observable<{ offer: OfferFull }> {
    return this.httpApi.patch(`/offers/${id}/unfollow`);
  }

  public duplicate$(id: number): Observable<{ offer: OfferFull }> {
    return this.httpApi.patch(`/offers/${id}/duplicate`);
  }

  public close(id: string | number): Observable<{ offer: OfferFull }> {
    return this.httpApi.patch(`/offers/${id}/close`);
  }

  public archive(id: string | number): Observable<{ offer: OfferFull }> {
    return this.httpApi.patch(`/offers/${id}/archive`);
  }

  public unarchive(id: string | number): Observable<{ offer: OfferFull }> {
    return this.httpApi.patch(`/offers/${id}/unarchive`);
  }

  public publish(id: string | number): Observable<{ offer: OfferFull }> {
    return this.httpApi.patch(`/offers/${id}/publish`);
  }

  public unpublish(id: string | number): Observable<{ offer: OfferFull }> {
    return this.httpApi.patch(`/offers/${id}/unpublish`);
  }

  public draft(id: string | number): Observable<{ offer: OfferFull }> {
    return this.httpApi.patch(`/offers/${id}/draft`);
  }

  public updatePositions$({ positions }: { positions: Position[] }): Observable<Position[]> {
    return this.httpApi
      .patch(`/offers/update_positions`, null, {
        offers: positions,
      })
      .pipe(map(() => positions));
  }

  public preview$({ data }: { data: OfferData }): Observable<{ previewUrl: string }> {
    let request;
    const body = { offer: data };

    if (data.id) {
      request = this.httpApi.patch(`/offers/${data.id}/preview`, null, body);
    } else {
      request = this.httpApi.post(`/offers/preview`, null, body);
    }

    return request;
  }

  public uploadSocialMediaImage$(
    id: number,
    file: File,
  ): Observable<{ id: number; fileName: string; url: string }> {
    const formData = new FormData();
    formData.append('sharing_image_upload', file);
    return this.httpApi.patch(`/offers/${id}/upload_sharing_image`, null, formData).pipe(
      map(response => ({
        ...response,
        id,
      })),
    );
  }

  public uploadCoverImage$(
    id: number,
    file: File,
  ): Observable<{ id: number; fileName: string; url: string }> {
    const formData = new FormData();
    formData.append('cover_image', file);
    return this.httpApi.post(`/offers/${id}/upload_cover_image`, null, formData).pipe(
      map(response => ({
        ...response,
        id,
      })),
    );
  }

  public updateSlug$(id: number, slug: string): Observable<{ offer: OfferFull }> {
    return this.httpApi.patch(`/offers/${id}/change_slug`, null, {
      slug,
    });
  }

  public insertDataFromTemplate$(
    offerId: number,
    jobTemplateId: number,
    insertMethod: InsertFromTemplateMethod,
    placementsMovements?: PlacementMovement[],
  ): Observable<{ offer: OfferFull }> {
    return this.httpApi.patch(
      `/offers/${offerId}/offer_templates/${jobTemplateId}/copy_data`,
      {},
      {
        scope: insertMethod,
        placementsMovements,
      },
    );
  }

  public switchJobsToProsemirror$(): Observable<void> {
    return this.httpApi.patch(`/bulk/offers/switch_wysiwyg_editors`);
  }
}
